import { useState, useEffect } from 'react';
import { initializePaddle } from '@paddle/paddle-js';

export const usePurchaseModal = (CREATE_PAYMENT_URL) => {
  const [paddle, setPaddle] = useState(null);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [priceError, setPriceError] = useState(null);

  // Initialize Paddle on component mount
  useEffect(() => {
    initializePaddle({
      environment: process.env.REACT_APP_PADDLE_ENVIRONMENT || 'sandbox',
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN || 'test_e9847087829aa869e305b4b48a5', // Client-side token (test_... or live_...)
    }).then((paddleInstance) => {
      if (paddleInstance) {
        setPaddle(paddleInstance);
      }
    });
  }, []);

  const PADDLE_PRICES = {
    PRICE_15: {
      priceId: process.env.REACT_APP_MUSIC_PRICE_15 || 'pri_01kegce41fqa6jpeh2xragn05e',
      amount: 14.99,
      label: '$14.99 USD'
    },
    PRICE_25: {
      priceId: process.env.REACT_APP_MUSIC_PRICE_25 || 'pri_01kec5my2km3gn0rqmnhdygwpn',
      amount: 24.99,
      label: '$24.99 USD'
    }
  };

  const handlePurchaseClick = (track, priceOption = null) => {
    setPriceError(null);
    setSelectedTrack(track);
    const defaultPrice = priceOption || PADDLE_PRICES.PRICE_15;
    setSelectedPrice(defaultPrice);
    setPurchaseSuccess(false);
    setShowPriceModal(true);
  };

  const validatePrice = (priceId) => {
    if (!priceId?.startsWith('pri_')) {
      setPriceError('Invalid price configuration. Please contact support.');
      return false;
    }
    return true;
  };

  const validateEmail = (email) => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setPriceError('Please use a valid email address.');
      return false;
    }
    return true;
  };

  const handleCheckout = async (e, userEmail, userId) => {
    e.preventDefault();
    setPriceError(null);

    if (!selectedTrack || !selectedPrice || !validateEmail(userEmail) || !validatePrice(selectedPrice.priceId)) {
      return;
    }

    if (!paddle) {
      setPriceError("Payment system is still loading. Please try again.");
      return;
    }

    setProcessingPayment(selectedTrack.id);

    try {
      const downloadUrl = selectedTrack.url || selectedTrack.downloadUrl || '';
      
      const body = {
        name: selectedTrack.name,
        email: userEmail,
        musicId: selectedTrack.id,
        priceId: selectedPrice.priceId,
        price: selectedPrice.amount,
        downloadUrl: downloadUrl,
        ...(userId && { userId })
      };

      const createResponse = await fetch(CREATE_PAYMENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const orderData = await createResponse.json();

      if (!createResponse.ok || orderData.error) {
        setPriceError(orderData.error || 'Payment creation failed.');
        setProcessingPayment(null);
        return;
      }

      // Store transaction ID for reference
      if (orderData.checkoutId) {
        localStorage.setItem('lastCheckoutId', orderData.checkoutId);
        
        paddle.Checkout.open({
          transactionId: orderData.checkoutId,
          customer: {
            email: userEmail
          },
          customData: {
            musicName: selectedTrack.name,
            artist: selectedTrack.artist || 'Unknown Artist',
            musicId: selectedTrack.id,
            description: `Purchase ${selectedTrack.name}`,
            price: selectedPrice.amount,
            priceId: selectedPrice.priceId,
            email: userEmail,
            downloadUrl: downloadUrl,
            userId: userId || null,
          },
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            successUrl: 'https://www.theasidors.com/payment-success'
          }
        });
      }

    } catch (error) {
      console.error('❌ Checkout error:', error);
      setPriceError('Network error creating payment. Please try again.');
    } finally {
      setProcessingPayment(null);
    }
  };

  const resetModal = () => {
    setShowPriceModal(false);
    setSelectedTrack(null);
    setSelectedPrice(null);
    setPurchaseSuccess(false);
    setProcessingPayment(null);
    setPriceError(null);
  };

  return {
    showPriceModal,
    setShowPriceModal,
    selectedTrack,
    selectedPrice,
    processingPayment,
    purchaseSuccess,
    priceError,
    PADDLE_PRICES,
    handlePurchaseClick,
    handleCheckout,
    resetModal,
    closeModal: resetModal
  };
};